import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import { PageHeading } from '@portal_components/components/PageHeading';
import { Contract, CustomerGroup, DetailsSearchPageForm, DetailsSearchResultPageItem } from '../types';
import { EHttpMethod, usePortalQuery } from '../hooks/usePortalQuery';
import { useEffect, useMemo, useState, useRef } from 'react';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import * as Yup from 'yup';
import { FormButton } from '@portal_components/components/FormButton';
import { FormDatePicker } from '@portal_components/components/FormDatePicker';
import { useStandingData } from '../hooks/useStandingData';
import { DetailsSearchResultList } from '../elements/Search/DetailsSearchResultList';
import { Button } from '@portal_components/components/Button';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { useSearchParams } from 'react-router-dom';

const PAGE_SIZE = 10;

const validationSchema = Yup.object<
  DetailsSearchPageForm,
  {
    [Key in keyof DetailsSearchPageForm]: Yup.AnySchema<DetailsSearchPageForm[Key]>;
  }
>({
  customerGroupId: Yup.string().required('Field is required'),
  contractId: Yup.string().required('Field is required')
});

export const DetailsSearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
  const [queryParams, setQueryParams] = useState<string>('');
  const [filtersShown, setFiltersShown] = useState<boolean>(false);
  const [filtersFormKey, setFiltersFormKey] = useState(0); // State variable for toggling

  const {
    standingData: { standingDataSiteStatusList }
  } = useStandingData();

  const { data: customerGroups, isLoading: customerGroupsLoading } = usePortalQuery<CustomerGroup[]>(
    EHttpMethod.GET,
    'customer-groups',
    'customer-groups',
    true,
    undefined,
    undefined,
    undefined,
    undefined,
    600000,
    600000
  );

  const methods = useForm<DetailsSearchPageForm>({
    // as any because ts doesn't think .reduce exists on the iterator
    defaultValues: (searchParams.entries() as any).reduce(
      (acc: DetailsSearchPageForm, [key, value]: [string, string]) => ({ ...acc, [key]: value }),
      {} as DetailsSearchPageForm
    ),
    resolver: yupResolver<DetailsSearchPageForm>(validationSchema as any)
  });

  const [customerGroupId, contractId] = methods.watch(['customerGroupId', 'contractId']);

  const resetFilters = () => {
    methods.setValue('dateRangeStart', undefined);
    methods.setValue('dateRangeEnd', undefined);
    methods.setValue('contractSiteStatusId', '');
    methods.setValue('siteReferenceName', '');
    methods.setValue('batchName', '');
    methods.setValue('postcode', '');

    setFiltersFormKey(prev => prev + 1); // Increment to force re-render of filters
  };

  useEffect(() => {
    setFormSubmitted(false);
    setQueryParams('');
  }, [customerGroupId, contractId]);

  useEffect(() => {
    // don't reset contract if the group is being set from search params
    if (
      contractId === searchParams.get('contractId') &&
      customerGroupId === searchParams.get('customerGroupId')
    )
      return;
    methods.setValue('contractId', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerGroupId]);

  const isInitialLoad = useRef(true);

  const shouldSubmitOnLoad = useMemo(() => {
    if (isInitialLoad.current) {
      setFiltersShown(true);
      isInitialLoad.current = false; // Mark as no longer the initial load
      return (searchParams.keys() as any).toArray().length > 0;
    }

    return false; // Prevent firing on subsequent updates (e.g., filter resets)
  }, [searchParams]);

  const { data: searchResult, isLoading: searchResultsLoading } = usePortalQuery<{
    page: DetailsSearchResultPageItem[];
    totalRows: number;
  }>(
    EHttpMethod.GET,
    `site-search/${contractId}?page=${currentPageNumber}&pageSize=${PAGE_SIZE}${queryParams}`,
    `site-search-${contractId}-${currentPageNumber}-${queryParams}`,
    formSubmitted && !!contractId && !!customerGroupId,
    undefined,
    undefined,
    undefined,
    undefined
  );

  const { data: customerContracts, isLoading: customerContractsLoading } = usePortalQuery<Contract[]>(
    EHttpMethod.GET,
    `customer-contracts/${customerGroupId}?filter=N`,
    `customer-contracts/${customerGroupId}?filter=N`,
    !!customerGroupId
  );

  const handleSearch = (data: DetailsSearchPageForm) => {
    setCurrentPageNumber(0);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { customerGroupId, contractId, ...rest } = data;
    const params = new URLSearchParams(Object.entries(rest).filter(([, val]) => !!val));
    const paramsString = params.toString();
    setQueryParams(paramsString ? `&${paramsString}` : '');
    setFormSubmitted(true);
    params.set('contractId', contractId);
    params.set('customerGroupId', customerGroupId);
    setSearchParams(params);
  };

  if (customerGroupsLoading) {
    return <div className="flex flex-row justify-center">Loading customer groups...</div>;
  }

  return (
    <div>
      <PageHeading
        title="Search for Site Details"
        navigationButtons={[
          {
            title: 'Back',
            path: '/',
            icon: '/back.svg',
            className: 'w-6 h-6'
          }
        ]}
      />
      <div className="grid grid-cols-4 gap-6">
        <div className="col-span-1 p-1">
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(handleSearch)}
              onLoad={shouldSubmitOnLoad ? methods.handleSubmit(handleSearch) : undefined}
            >
              <FormSearchDropdown
                title="Customer Group Name"
                name="customerGroupId"
                placeholder="Select Customer Group"
                mandatory
                elementsForDisplay={
                  customerGroups
                    ? customerGroups.map(({ customerGroupName, customerGroupId }) => ({
                        title: customerGroupName,
                        value: customerGroupId
                      }))
                    : []
                }
              />

              <FormDropdown
                title="Contract"
                name="contractId"
                placeholder="Select Contract"
                mandatory
                elementsForDisplay={
                  customerContracts
                    ? customerContracts.map(({ contractId, contractType }) => ({
                        title: `${contractType} (Contract ID: ${contractId})`,
                        value: contractId
                      }))
                    : []
                }
                readonly={customerContractsLoading || customerGroupId === undefined}
              />
              {customerGroupId &&
                !customerContractsLoading &&
                customerContracts &&
                customerContracts.length === 0 && (
                  <div className="pb-4 text-feedback-red inline-block">
                    {'This customer group has no active contracts.'}
                  </div>
                )}
              <div
                className="cursor-pointer flex flex-row justify-between mb-3 px-1 pb-1 border-b-2 border-secondary-deep-blue-80"
                onClick={() => setFiltersShown(!filtersShown)}
              >
                <p className="text-xs pl-1">{filtersShown ? 'Hide Filters' : 'Show Filters'}</p>
                <img
                  src={filtersShown ? '/arrowUp.svg' : '/arrowDown.svg'}
                  alt={filtersShown ? 'Hide Filters' : 'Show Filters'}
                  className="w-3 h-3"
                />
              </div>
              {filtersShown && (
                <div className="grid grid-cols-2 gap-2" key={filtersFormKey}>
                  <FormDatePicker
                    title="Date Range Start"
                    name="dateRangeStart"
                    readonly={!contractId || !customerGroupId}
                  />
                  <FormDatePicker
                    title="Date Range End"
                    name="dateRangeEnd"
                    readonly={!contractId || !customerGroupId}
                  />
                  <FormDropdown
                    title="Site Status"
                    name="contractSiteStatusId"
                    placeholder="Select Status"
                    elementsForDisplay={standingDataSiteStatusList}
                    readonly={!contractId || !customerGroupId}
                  />
                  <FormTextInput
                    title="Site Reference Name"
                    name="siteReferenceName"
                    readonly={!contractId || !customerGroupId}
                  />
                  <FormTextInput
                    title="Batch Name"
                    name="batchName"
                    readonly={!contractId || !customerGroupId}
                  />
                  <FormTextInput
                    title="Postcode"
                    name="postcode"
                    readonly={!contractId || !customerGroupId}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <FormButton type="submit" primary className="w-full" disabled={!contractId}>
                  Search
                </FormButton>
                <Button text="Reset Filters" primary={false} onClick={resetFilters} className="w-full" />
              </div>
            </form>
          </FormProvider>
        </div>
        <div className="col-span-3">
          {searchResultsLoading && formSubmitted && (
            <div className="flex flex-row justify-center">Loading meter point details...</div>
          )}
          {formSubmitted && !searchResultsLoading && !searchResult && (
            <div className="flex flex-row justify-center">No meter point details found</div>
          )}
          {formSubmitted && !searchResultsLoading && (
            <DetailsSearchResultList
              contractId={contractId}
              customerGroupId={customerGroupId}
              numberOfResults={searchResult?.totalRows ?? 0}
              pageData={searchResult?.page ?? []}
            />
          )}
          {formSubmitted && !!searchResult?.totalRows && (
            <div className="flex flex-row justify-between pt-4">
              <Button
                onClick={() => setCurrentPageNumber(prev => prev - 1)}
                disabled={currentPageNumber === 0}
                primary
                text="Previous"
              />
              <Button
                onClick={() => setCurrentPageNumber(prev => prev + 1)}
                disabled={
                  (searchResult?.totalRows ?? 0) < PAGE_SIZE ||
                  currentPageNumber >= Math.ceil((searchResult?.totalRows ?? 0) / PAGE_SIZE) - 1
                }
                primary
                text="Next"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
